import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Category } from 'models/categories';
import { useAppStore } from 'hooks/state/useAppStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { TreeSelect } from 'components/treeselect/TreeSelect';
import { TreeItemWithIcon } from 'components/treeselect/types';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';

interface CategorySelectProps {
  selectedCategories?: string[];
  setSelectedCategories?: (value: string[]) => void;
  className?: string;
  showConfirmButton?: boolean;
}

export function CategorySelect({
  selectedCategories = undefined,
  setSelectedCategories = undefined,
  className = undefined,
}: CategorySelectProps) {
  const intl = useIntl();

  const { allCategories } = useTransitiveCacheStore();
  const { categories, setCategories } = useAppStore();

  const { disabledFilters } = useTransitiveCacheStore();

  const sortItemLevel = (treeItems: TreeItemWithIcon[]) => treeItems.sort((a, b) => a.label.localeCompare(b.label));

  const categoryToTreeViewBaseItem = (category: Category): TreeItemWithIcon => {
    // If we have exactly one child, let's not show the parent at all
    const cat = category.subCategories && category.subCategories.length === 1 ? category.subCategories[0] : category;
    return {
      id: cat.categoryCode,
      label: intl.formatMessage({
        id: `product.categories.${cat.categoryCode}`,
        defaultMessage: cat.categoryCode,
      }),
      icon: findIconForCategory(cat.categoryCode, 20),
      children: sortItemLevel(cat.subCategories ? cat.subCategories.map(c => categoryToTreeViewBaseItem(c)) : []),
    };
  };

  const { allOptions, leafCategoryIds } = useMemo(() => {
    const leafIds = new Set<string>();
    const collectLeafIds = (category: Category) => {
      if (!category.subCategories?.length) leafIds.add(category.categoryCode);
      else category.subCategories.forEach(collectLeafIds);
    };
    allCategories.forEach(collectLeafIds);

    return {
      allOptions: sortItemLevel(allCategories.map(categoryToTreeViewBaseItem)),
      leafCategoryIds: leafIds,
    };
  }, [allCategories]);

  const treeSelectedCategories = useMemo(() => {
    const selected = new Set(
      (selectedCategories ?? categories ?? []).filter(categoryId => leafCategoryIds.has(categoryId))
    );
    const addSelectedParents = (items: TreeItemWithIcon[]) => {
      items.forEach(item => {
        const children = item.children ?? [];
        addSelectedParents(children);
        if (children.length > 0 && children.every(child => selected.has(child.id))) selected.add(item.id);
      });
    };
    addSelectedParents(allOptions);
    return [...selected];
  }, [allOptions, categories, leafCategoryIds, selectedCategories]);

  const handleSelectedItemsChange = (itemIds: string[]) => {
    (setSelectedCategories ?? setCategories)(itemIds.filter(categoryId => leafCategoryIds.has(categoryId)));
  };

  return (
    <TreeSelect
      disabled={disabledFilters.includes('prdCategory')}
      id="category-filter"
      className={className}
      label={intl.formatMessage({
        id: 'filter.key.selectedCategories',
        defaultMessage: 'Categories',
      })}
      items={allOptions}
      selectedItems={treeSelectedCategories}
      setSelectedItems={handleSelectedItemsChange}
      showConfirmButton
    />
  );
}
